import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import {
  associateWebAuthnCredential,
  AuthUser,
  getCurrentUser,
  listWebAuthnCredentials,
  setUpTOTP,
  signOut,
  SignUpInput,
  updateMFAPreference,
  verifyTOTPSetup,
  fetchMFAPreference,
  AuthWebAuthnCredential,
} from "aws-amplify/auth";
import { deleteWebAuthnCredential } from "aws-amplify/auth";
import User, { UserSignUp } from "../types/user";
import { getCompany } from "./company";

interface AuthState {
  status: "idle" | "loading" | "failed";
  awsUser?: AuthUser;
  dbUser?: User;
  loggedIn: boolean;
  passkeyFailed: boolean;
  mfaEnabled?: boolean;
  authCredentials?: AuthWebAuthnCredential[];
  securityPreference?: "mfa" | "passkey" | "none";
}

const initialState: AuthState = {
  status: "idle",
  loggedIn: false,
  passkeyFailed: false,
};

export const logout = createAsyncThunk("auth/logout", async () => {
  await http.get(`/auth/logout`).then(async () => {
    await signOut({ global: true });
    window.location.href = `${window.location.origin}/auth/redirect`;
  });
});

export const checkMfaEnabled = createAsyncThunk("auth/check-mfa", async () => {
  const mfaPreference = await fetchMFAPreference();
  const prefersMfa =
    Array.isArray(mfaPreference?.preferred) &&
    mfaPreference.preferred?.includes("TOTP");
  const hasTotpEnabled =
    Array.isArray(mfaPreference?.enabled) &&
    mfaPreference.enabled.includes("TOTP");
  return { enabled: hasTotpEnabled, preferred: prefersMfa };
});

export const getPasskeys = createAsyncThunk("auth/check-passkeys", async () => {
  const { credentials = [] } = await listWebAuthnCredentials();
  return credentials;
});

export const registerPasskey = createAsyncThunk(
  "auth/register-passkey",
  async () => {
    try {
      await associateWebAuthnCredential();
    } catch (e) {
      console.error("associateWebAuthnCredential failed", e);
    } finally {
      try {
        const list = await listWebAuthnCredentials();
        return list;
      } catch (e) {
        console.error("listWebAuthnCredentials failed", e);
        throw e;
      }
    }
  },
);

export const deletePasskey = createAsyncThunk(
  "auth/delete-passkey",
  async (credentialId: string) => {
    try {
      await deleteWebAuthnCredential({ credentialId });
    } catch (e) {
      console.error("deleteWebAuthnCredential failed", e);
    } finally {
      try {
        const list = await listWebAuthnCredentials();
        return list;
      } catch (e) {
        console.error("listWebAuthnCredentials failed", e);
        throw e;
      }
    }
  },
);

export const setUpMfa = createAsyncThunk(
  "auth/register-mfa",
  async (username: string) => {
    try {
      const totpSetupDetails = await setUpTOTP();
      const appName = "SecureVizual";
      const url = totpSetupDetails.getSetupUri(appName, username);

      return url;
    } catch (e) {
      console.error("setup MFA failed", e);
    }

    return "";
  },
);

export const verifyMfa = createAsyncThunk<boolean, string>(
  "auth/verify-mfa",
  async (code: string) => {
    try {
      await verifyTOTPSetup({ code: code });
    } catch (e) {
      console.error("verify MFA failed", e);
    } finally {
      try {
        await updateMFAPreference({ totp: "ENABLED" });
        return true;
      } catch (error) {
        return false;
      }
    }
  },
);

export const fetchAwsUser = createAsyncThunk<AuthUser>(
  "auth/fetch-profile",
  async () => {
    const response = await getCurrentUser();
    return response;
  },
);

export const companyLogin = createAsyncThunk<User, number>(
  "auth/fetch-user",
  async (companyId: number) => {
    const response = await http.get<User>(`/auth?companyId=${companyId}`, {
      params: {
        refreshToken: true,
      },
    });
    return response.data;
  },
);

export const userSignUp = createAsyncThunk<User, SignUpInput>(
  "auth/sign-up",
  async (user: SignUpInput) => {
    let newUser: UserSignUp = {
      email: user.username,
      password: user.password!,
      firstName: user.options?.userAttributes.given_name!,
      lastName: user.options?.userAttributes.family_name!,
      userId: user.options?.userAttributes.preferred_username!,
    };

    const response = await http.post<User>(`/auth`, newUser);
    return response.data;
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setSecureLogin(state, action) {
      state.passkeyFailed = action.payload;
    },
    clearDbUser(state) {
      state.dbUser = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkMfaEnabled.pending, (state) => {
        state.status = "loading";
      })
      .addCase(checkMfaEnabled.fulfilled, (state, action) => {
        state.status = "idle";
        state.mfaEnabled = action.payload.enabled;
        if (action.payload.preferred) state.securityPreference = "mfa";
      })
      .addCase(checkMfaEnabled.rejected, (state) => {
        state.status = "failed";
      })

      .addCase(getPasskeys.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getPasskeys.fulfilled, (state, action) => {
        state.status = "idle";
        state.authCredentials = action.payload;
      })
      .addCase(getPasskeys.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(registerPasskey.pending, (state) => {
        state.status = "loading";
      })
      .addCase(registerPasskey.fulfilled, (state, action) => {
        state.status = "idle";
        state.authCredentials = action.payload?.credentials;
      })
      .addCase(registerPasskey.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(setUpMfa.pending, (state) => {
        state.status = "loading";
      })
      .addCase(setUpMfa.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(setUpMfa.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(verifyMfa.pending, (state) => {
        state.status = "loading";
      })
      .addCase(verifyMfa.fulfilled, (state, action) => {
        state.status = "idle";
        state.mfaEnabled = true;
      })
      .addCase(verifyMfa.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(logout.pending, (state) => {
        state.status = "loading";
      })
      .addCase(logout.fulfilled, (state) => {
        state.status = "idle";
        state.loggedIn = false;
        state.awsUser = undefined;
        state.authCredentials = undefined;
      })
      .addCase(logout.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(fetchAwsUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAwsUser.fulfilled, (state, action) => {
        state.status = "idle";
        state.loggedIn = true;
        state.awsUser = action.payload;
      })
      .addCase(fetchAwsUser.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(companyLogin.pending, (state) => {
        state.status = "loading";
      })
      .addCase(companyLogin.fulfilled, (state, action) => {
        state.status = "idle";
        state.dbUser = action.payload;
      })
      .addCase(companyLogin.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(userSignUp.pending, (state) => {
        state.status = "loading";
      })
      .addCase(userSignUp.fulfilled, (state, action) => {
        state.status = "idle";
        state.dbUser = action.payload;
      })
      .addCase(userSignUp.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(deletePasskey.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deletePasskey.fulfilled, (state, action) => {
        state.status = "idle";
        const credentials = action.payload?.credentials;
        state.authCredentials = credentials;
      })
      .addCase(deletePasskey.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getCompany.fulfilled, (state, action) => {
        if (!action.payload) {
          state.dbUser = initialState.dbUser;
        }
      });
  },
});

export const { clearDbUser, setSecureLogin } = authSlice.actions;

export default authSlice.reducer;
